import React, { useState, useEffect } from 'react';
import AdminManagementService from '../../services/adminManagementService';

const CreateAdminModal = ({ onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        role: 'support_admin'
    });
    const [loading, setLoading] = useState(false);
    const [permissionTemplates, setPermissionTemplates] = useState({});

    // Load permission templates
    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const response = await AdminManagementService.getPermissionTemplates();
                if (response.success) {
                    setPermissionTemplates(response.data);
                }
            } catch (error) {
                console.error('Error loading permission templates:', error);
            }
        };
        loadTemplates();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            alert('Passwords do not match!');
            return;
        }

        if (formData.password.length < 6) {
            alert('Password must be at least 6 characters long!');
            return;
        }

        setLoading(true);
        try {
            // Get permissions based on selected role
            const permissions = permissionTemplates[formData.role] || {};
            
            await onSubmit({
                email: formData.email,
                password: formData.password,
                role: formData.role,
                permissions: permissions
            });
        } catch (error) {
            console.error('Error in form submission:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const getRoleDescription = (role) => {
        switch (role) {
            case 'super_admin': return 'Full system access with all permissions';
            case 'minister_admin': return 'Content and user management with limited financial access';
            case 'support_admin': return 'Customer support with basic user viewing capabilities';
            default: return '';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Create New Admin</h2>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="admin@coolprofessors.com"
                        />
                    </div>

                    {/* Role Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Role
                        </label>
                        <select
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="support_admin">Support Admin</option>
                            <option value="minister_admin">Minister Admin</option>
                            <option value="super_admin">Super Admin</option>
                        </select>
                        <p className="mt-1 text-sm text-gray-500">
                            {getRoleDescription(formData.role)}
                        </p>
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Password
                        </label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            minLength="6"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter password"
                        />
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Password
                        </label>
                        <input
                            type="password"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            minLength="6"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Confirm password"
                        />
                    </div>

                    {/* Permissions Preview */}
                    {permissionTemplates[formData.role] && (
                        <div className="bg-gray-50 p-3 rounded-md">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Permissions:</h4>
                            <div className="text-sm text-gray-600 space-y-1">
                                {Object.entries(permissionTemplates[formData.role]).map(([key, value]) => (
                                    <div key={key} className="flex items-center">
                                        <span className={`w-2 h-2 rounded-full mr-2 ${value ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                        <span>{key.replace(/_/g, ' ')}: {value ? 'Yes' : 'No'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? 'Creating...' : 'Create Admin'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateAdminModal;